/**
 * Geburtsland → Zeitzone für das Human-Design-Chart.
 * (24.09.2026, Ticket 2567, Onboarding-Plan Stufe 0b)
 *
 * ## Warum ein Land und kein Ort
 *
 * Bis heute leitete `timezoneForPlace()` die Zeitzone aus dem Geburtsort ab —
 * und lieferte in JEDEM Zweig Europe/Vienna. Wer in Berlin oder New York
 * geboren ist, bekam ein Chart mit der falschen Weltzeit: eine Stunde daneben
 * verschiebt den Mond um eine halbe Linie, sechs Stunden können Typ und
 * Autorität kippen.
 *
 * Einen Ort in eine Zeitzone zu übersetzen hieße Geocoding (fremder Dienst,
 * Rateweg). Die Manifestor-Akademie fragt stattdessen das LAND ab, aus einer
 * festen Liste mit IANA-Zone je Eintrag. Die Sommerzeit und die historischen
 * Regeln (Österreich 1975 ohne Sommerzeit) rechnet `zoneinfo` im Rechenkern.
 *
 * ## Woher die Liste kommt
 *
 * Die 45 Einträge ohne Vermerk sind wörtlich aus `api/chart.php` und
 * `chart.html` der Manifestor-Akademie (S7, Stand 0.18.0) übernommen — dort
 * geprüft und mit demselben Rechenkern im Einsatz. Ergänzt am 24.09.2026:
 *
 *   - Regionen für Länder mit mehreren Zeitzonen (USA, Kanada, Mexiko,
 *     Brasilien, Russland, Australien) — vorher gab es dort nur eine Stadt;
 *   - Länder mit genau einer Zone, aus denen viele Menschen in Österreich
 *     stammen und die sonst nur über ein Nachbarland wählbar wären
 *     (Bosnien und Herzegowina, Kosovo, Montenegro, Nordmazedonien,
 *     Albanien, Liechtenstein, Luxemburg, Syrien, Afghanistan, Iran, Irak).
 *
 * Jede Zone ist gegen Node (Intl) und den Rechenkern (Python zoneinfo)
 * geprüft; der Test `tests/shared/geburtsland.test.ts` hält das fest.
 *
 * ## Vorgabe
 *
 * Wer kein Land gesetzt hat, rechnet wie bisher mit Österreich — so ändert
 * sich für keinen bestehenden Menschen das Chart, bis jemand das Land setzt.
 *
 * ⚠️ Der Schlüssel (`AT`, `US-OST`) steht in der Datenbank, nicht die Zone.
 *    So lässt sich eine Zone später korrigieren, ohne Zeilen umzuschreiben.
 *    Schlüssel darum NIE umbenennen — nur neue dazulegen.
 */

export interface Geburtsland {
  /** Gespeicherter Schlüssel: ISO-Ländercode, bei Regionen mit Zusatz. */
  schluessel: string;
  /** Deutscher Ländername, bei Regionen für alle Regionen gleich. */
  land: string;
  /** Nur bei Ländern mit mehreren Zonen. */
  region?: string;
  /** IANA-Zeitzone für den Rechenkern. */
  zone: string;
  /** Andere Schreibweisen, unter denen das Land gefunden wird. */
  auch?: readonly string[];
}

export const VORGABE_GEBURTSLAND = "AT";

/**
 * Reihenfolge: die drei häufigsten zuerst, dann alphabetisch; die Weltzeit
 * zuletzt. `geburtslaenderSortiert()` baut daraus die Auswahl.
 */
export const GEBURTSLAENDER: readonly Geburtsland[] = [
  { schluessel: "AT", land: "Österreich", zone: "Europe/Vienna", auch: ["Austria", "Oesterreich"] },
  { schluessel: "DE", land: "Deutschland", zone: "Europe/Berlin", auch: ["Germany", "BRD", "DDR"] },
  { schluessel: "CH", land: "Schweiz", zone: "Europe/Zurich", auch: ["Switzerland"] },

  { schluessel: "EG", land: "Ägypten", zone: "Africa/Cairo" },
  { schluessel: "AF", land: "Afghanistan", zone: "Asia/Kabul" },
  { schluessel: "AL", land: "Albanien", zone: "Europe/Tirane" },
  { schluessel: "BE", land: "Belgien", zone: "Europe/Brussels" },
  { schluessel: "BA", land: "Bosnien und Herzegowina", zone: "Europe/Sarajevo", auch: ["Bosnien"] },
  { schluessel: "BR-SP", land: "Brasilien", region: "São Paulo, Rio, Brasília", zone: "America/Sao_Paulo" },
  { schluessel: "BR-AM", land: "Brasilien", region: "Amazonas (Manaus)", zone: "America/Manaus" },
  { schluessel: "BG", land: "Bulgarien", zone: "Europe/Sofia" },
  { schluessel: "CN", land: "China", zone: "Asia/Shanghai" },
  { schluessel: "DK", land: "Dänemark", zone: "Europe/Copenhagen" },
  { schluessel: "FI", land: "Finnland", zone: "Europe/Helsinki" },
  { schluessel: "FR", land: "Frankreich", zone: "Europe/Paris" },
  { schluessel: "GR", land: "Griechenland", zone: "Europe/Athens" },
  { schluessel: "GB", land: "Großbritannien", zone: "Europe/London", auch: ["England", "Vereinigtes Königreich", "UK"] },
  { schluessel: "IN", land: "Indien", zone: "Asia/Kolkata" },
  { schluessel: "IQ", land: "Irak", zone: "Asia/Baghdad" },
  { schluessel: "IR", land: "Iran", zone: "Asia/Tehran" },
  { schluessel: "IE", land: "Irland", zone: "Europe/Dublin" },
  { schluessel: "IL", land: "Israel", zone: "Asia/Jerusalem" },
  { schluessel: "IT", land: "Italien", zone: "Europe/Rome" },
  { schluessel: "JP", land: "Japan", zone: "Asia/Tokyo" },
  { schluessel: "CA-BC", land: "Kanada", region: "Pazifik (Vancouver)", zone: "America/Vancouver" },
  { schluessel: "CA-AB", land: "Kanada", region: "Gebirge (Edmonton, Calgary)", zone: "America/Edmonton" },
  { schluessel: "CA-SK", land: "Kanada", region: "Saskatchewan (Regina)", zone: "America/Regina" },
  { schluessel: "CA-MB", land: "Kanada", region: "Mitte (Winnipeg)", zone: "America/Winnipeg" },
  { schluessel: "CA-TOR", land: "Kanada", region: "Osten (Toronto, Montréal)", zone: "America/Toronto" },
  { schluessel: "CA-NS", land: "Kanada", region: "Atlantik (Halifax)", zone: "America/Halifax" },
  { schluessel: "CA-NL", land: "Kanada", region: "Neufundland (St. John’s)", zone: "America/St_Johns" },
  { schluessel: "XK", land: "Kosovo", zone: "Europe/Belgrade" },
  { schluessel: "HR", land: "Kroatien", zone: "Europe/Zagreb" },
  { schluessel: "LI", land: "Liechtenstein", zone: "Europe/Vaduz" },
  { schluessel: "LU", land: "Luxemburg", zone: "Europe/Luxembourg" },
  { schluessel: "MX-CDMX", land: "Mexiko", region: "Mitte (Mexiko-Stadt)", zone: "America/Mexico_City" },
  { schluessel: "MX-PAZ", land: "Mexiko", region: "Pazifik (Mazatlán)", zone: "America/Mazatlan" },
  { schluessel: "MX-BC", land: "Mexiko", region: "Nordwest (Tijuana)", zone: "America/Tijuana" },
  { schluessel: "MX-QR", land: "Mexiko", region: "Quintana Roo (Cancún)", zone: "America/Cancun" },
  { schluessel: "ME", land: "Montenegro", zone: "Europe/Podgorica" },
  { schluessel: "NZ", land: "Neuseeland", zone: "Pacific/Auckland" },
  { schluessel: "NL", land: "Niederlande", zone: "Europe/Amsterdam", auch: ["Holland"] },
  { schluessel: "MK", land: "Nordmazedonien", zone: "Europe/Skopje", auch: ["Mazedonien"] },
  { schluessel: "NO", land: "Norwegen", zone: "Europe/Oslo" },
  { schluessel: "PL", land: "Polen", zone: "Europe/Warsaw" },
  { schluessel: "PT", land: "Portugal", zone: "Europe/Lisbon" },
  { schluessel: "RO", land: "Rumänien", zone: "Europe/Bucharest" },
  { schluessel: "RU-MOW", land: "Russland", region: "Moskau", zone: "Europe/Moscow" },
  { schluessel: "RU-KGD", land: "Russland", region: "Kaliningrad", zone: "Europe/Kaliningrad" },
  { schluessel: "RU-SAM", land: "Russland", region: "Samara", zone: "Europe/Samara" },
  { schluessel: "RU-JEK", land: "Russland", region: "Jekaterinburg", zone: "Asia/Yekaterinburg" },
  { schluessel: "RU-OMS", land: "Russland", region: "Omsk", zone: "Asia/Omsk" },
  { schluessel: "RU-NOW", land: "Russland", region: "Nowosibirsk", zone: "Asia/Novosibirsk" },
  { schluessel: "RU-KRA", land: "Russland", region: "Krasnojarsk", zone: "Asia/Krasnoyarsk" },
  { schluessel: "RU-IRK", land: "Russland", region: "Irkutsk", zone: "Asia/Irkutsk" },
  { schluessel: "RU-JAK", land: "Russland", region: "Jakutsk", zone: "Asia/Yakutsk" },
  { schluessel: "RU-WLA", land: "Russland", region: "Wladiwostok", zone: "Asia/Vladivostok" },
  { schluessel: "SE", land: "Schweden", zone: "Europe/Stockholm" },
  { schluessel: "RS", land: "Serbien", zone: "Europe/Belgrade" },
  { schluessel: "SK", land: "Slowakei", zone: "Europe/Bratislava" },
  { schluessel: "SI", land: "Slowenien", zone: "Europe/Ljubljana" },
  { schluessel: "ES", land: "Spanien", zone: "Europe/Madrid" },
  { schluessel: "ZA", land: "Südafrika", zone: "Africa/Johannesburg" },
  { schluessel: "SY", land: "Syrien", zone: "Asia/Damascus" },
  { schluessel: "TH", land: "Thailand", zone: "Asia/Bangkok" },
  { schluessel: "CZ", land: "Tschechien", zone: "Europe/Prague" },
  { schluessel: "TR", land: "Türkei", zone: "Europe/Istanbul" },
  { schluessel: "UA", land: "Ukraine", zone: "Europe/Kyiv" },
  { schluessel: "HU", land: "Ungarn", zone: "Europe/Budapest" },
  { schluessel: "US-OST", land: "USA", region: "Ostküste (New York)", zone: "America/New_York" },
  { schluessel: "US-MITTE", land: "USA", region: "Mitte (Chicago)", zone: "America/Chicago" },
  { schluessel: "US-GEBIRGE", land: "USA", region: "Gebirge (Denver)", zone: "America/Denver" },
  { schluessel: "US-AZ", land: "USA", region: "Arizona (Phoenix)", zone: "America/Phoenix" },
  { schluessel: "US-WEST", land: "USA", region: "Westküste (Los Angeles)", zone: "America/Los_Angeles" },
  { schluessel: "US-AK", land: "USA", region: "Alaska", zone: "America/Anchorage" },
  { schluessel: "US-HI", land: "USA", region: "Hawaii", zone: "Pacific/Honolulu" },
  { schluessel: "AE", land: "Vereinigte Arabische Emirate", zone: "Asia/Dubai", auch: ["VAE", "Emirate"] },
  { schluessel: "AU-SYD", land: "Australien", region: "Neusüdwales (Sydney)", zone: "Australia/Sydney" },
  { schluessel: "AU-MEL", land: "Australien", region: "Victoria (Melbourne)", zone: "Australia/Melbourne" },
  { schluessel: "AU-BNE", land: "Australien", region: "Queensland (Brisbane)", zone: "Australia/Brisbane" },
  { schluessel: "AU-ADL", land: "Australien", region: "Südaustralien (Adelaide)", zone: "Australia/Adelaide" },
  { schluessel: "AU-DRW", land: "Australien", region: "Nordterritorium (Darwin)", zone: "Australia/Darwin" },
  { schluessel: "AU-PER", land: "Australien", region: "Westaustralien (Perth)", zone: "Australia/Perth" },
  { schluessel: "AU-HOB", land: "Australien", region: "Tasmanien (Hobart)", zone: "Australia/Hobart" },

  { schluessel: "UTC", land: "Weltzeit (UTC)", zone: "UTC" },
];

/** „USA — Ostküste (New York)" bzw. „Österreich". */
export function geburtslandName(g: Geburtsland): string {
  return g.region ? `${g.land} — ${g.region}` : g.land;
}

const NACH_SCHLUESSEL = new Map(GEBURTSLAENDER.map((g) => [g.schluessel, g]));

/** Der Eintrag zum gespeicherten Schlüssel; null bei fehlendem/unbekanntem. */
export function geburtslandVon(schluessel: string | null | undefined): Geburtsland | null {
  if (!schluessel) return null;
  return NACH_SCHLUESSEL.get(schluessel.trim().toUpperCase()) ?? null;
}

/** Die Zone für einen Schlüssel — ohne gültigen Schlüssel die Vorgabe (Österreich). */
export function zoneFuerGeburtsland(schluessel: string | null | undefined): string {
  return (geburtslandVon(schluessel) ?? geburtslandVon(VORGABE_GEBURTSLAND))?.zone ?? "Europe/Vienna";
}

/** Klein, Umlaute ausgeschrieben, ohne Akzente und Satzzeichen. */
function vergleichbar(s: string): string {
  return s
    .normalize("NFC")
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/**
 * Findet ein Land aus freier Eingabe: Schlüssel („DE"), Name („Deutschland",
 * „USA — Ostküste (New York)"), andere Schreibweise („Germany") oder eine
 * IANA-Zone der Liste („Europe/Berlin"). Ein Land mit mehreren Regionen ist
 * über seinen Namen allein NICHT eindeutig → null; `regionenVon` nennt dann
 * die Auswahl. Für Werkzeuge und Sprache — die Formulare schicken Schlüssel.
 */
export function geburtslandFinden(eingabe: string | null | undefined): Geburtsland | null {
  const roh = (eingabe ?? "").trim();
  if (!roh) return null;
  const direkt = geburtslandVon(roh);
  if (direkt) return direkt;
  const perZone = GEBURTSLAENDER.find((g) => g.zone === roh);
  if (perZone) return perZone;
  const v = vergleichbar(roh);
  if (!v) return null;
  const voll = GEBURTSLAENDER.find((g) => vergleichbar(geburtslandName(g)) === v);
  if (voll) return voll;
  const treffer = GEBURTSLAENDER.filter(
    (g) => vergleichbar(g.land) === v || (g.auch ?? []).some((a) => vergleichbar(a) === v),
  );
  return treffer.length === 1 ? (treffer[0] ?? null) : null;
}

/** Die Regionen eines Landes mit mehreren Zonen (leer, wenn es nur eine gibt). */
export function regionenVon(land: string | null | undefined): Geburtsland[] {
  const v = vergleichbar(land ?? "");
  if (!v) return [];
  const alle = GEBURTSLAENDER.filter((g) => vergleichbar(g.land) === v);
  return alle.length > 1 ? alle : [];
}

/** Für Auswahllisten: Vorgabe-Länder oben, dann nach Namen, Weltzeit zuletzt. */
export function geburtslaenderSortiert(): Geburtsland[] {
  const oben = ["AT", "DE", "CH"];
  const kopf = oben.map((s) => NACH_SCHLUESSEL.get(s)).filter((g): g is Geburtsland => Boolean(g));
  const rest = GEBURTSLAENDER.filter((g) => !oben.includes(g.schluessel) && g.schluessel !== "UTC").sort(
    (a, b) => geburtslandName(a).localeCompare(geburtslandName(b), "de"),
  );
  const utc = NACH_SCHLUESSEL.get("UTC");
  return [...kopf, ...rest, ...(utc ? [utc] : [])];
}

/**
 * Für Schreibwege (Profil, Admin, Werkzeuge): was aus einem Eingabewert
 * gespeichert wird. `undefined` = nichts ändern (nicht mitgeschickt oder
 * unbekannt — ein unbekanntes Land soll nicht das ganze Speichern kippen),
 * `null` = Land löschen (zurück zur Vorgabe), sonst der Schlüssel.
 */
export function geburtslandZumSpeichern(v: unknown): string | null | undefined {
  if (v === null) return null;
  if (typeof v !== "string") return undefined;
  if (!v.trim()) return null;
  return geburtslandFinden(v)?.schluessel;
}
